import React, { useState, useRef } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";

/**
 * ImageUpload Component - PLATFORM STANDARD
 * 
 * This component should be used for ALL image fields across the platform.
 * DO NOT use text inputs (Input) for image URLs - always use this upload component.
 * 
 * See /app/memory/PLATFORM_RULES.md for more details.
 */
export default function ImageUpload({ 
  label, 
  value, 
  onChange, 
  description,
  previewSize = "medium", // "small" | "medium" | "large"
  aspectRatio = "auto", // "auto" | "square" | "16:9" | "banner"
  className = ""
}) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await fetch(`${API}/cms/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        onChange(data.url);
        toast.success("Image uploaded!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemove = () => {
    if (!window.confirm("Are you sure you want to remove this image?")) return;
    onChange(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast.success("Image removed");
  };

  // Size configurations
  const sizeConfig = {
    small: { container: "w-16 h-16", preview: "w-16 h-16" },
    medium: { container: "w-24 h-24", preview: "w-24 h-24" },
    large: { container: "w-32 h-20", preview: "w-32 h-20" },
    banner: { container: "w-full h-24", preview: "w-full h-24" }
  };

  const aspectConfig = {
    auto: "",
    square: "aspect-square",
    "16:9": "aspect-video",
    banner: "aspect-[4/1]"
  };

  const size = sizeConfig[previewSize] || sizeConfig.medium;

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <Label className="text-sm font-medium">{label}</Label>}
      {description && <p className="text-xs text-gray-500">{description}</p>}
      
      <div className="flex items-start gap-3">
        {/* Preview or Upload Area */}
        {value ? (
          <div className={`relative rounded-lg border overflow-hidden bg-gray-50 ${size.preview} ${aspectConfig[aspectRatio]}`}>
            <img 
              src={value} 
              alt={label || "Preview"} 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <button
              onClick={handleRemove}
              className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-md transition-colors"
              title="Remove image"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors ${size.container} ${aspectConfig[aspectRatio]} ${uploading ? 'cursor-wait' : ''}`}
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
            ) : (
              <>
                <Upload className="w-5 h-5 text-gray-400 mb-1" />
                <span className="text-xs text-gray-400">Upload</span>
              </>
            )}
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="hidden"
        />

        {/* Upload/Change button when image exists */}
        {value && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <ImageIcon className="w-4 h-4 mr-1" />
                Change
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Compact Image Upload - for smaller spaces
 */
export function CompactImageUpload({ value, onChange, placeholder = "Upload image" }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await fetch(`${API}/cms/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        onChange(data.url);
        toast.success("Image uploaded!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-2">
      {value ? (
        <>
          <div className="w-10 h-10 rounded border overflow-hidden bg-gray-50">
            <img src={value} alt="Preview" className="w-full h-full object-cover" />
          </div>
          <Button variant="ghost" size="sm" onClick={() => {
            if (!window.confirm("Are you sure you want to remove this image?")) return;
            onChange(null);
            toast.success("Image removed");
          }}>
            <X className="w-4 h-4 text-red-500" />
          </Button>
        </>
      ) : (
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Upload className="w-4 h-4 mr-1" />
              {placeholder}
            </>
          )}
        </Button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />
    </div>
  );
}
